import React, { useState, useEffect } from 'react';
import {
  X,
  FileText,
  CreditCard,
  Calendar,
  ChevronDown,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import styles from './PaymentReceivedDrawer.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';

const PaymentReceivedDrawer = ({ isOpen, onClose, onSave, paymentId }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    customer_id: '',
    sales_invoice_id: '',
    invoice_ref: '',
    invoice_date: '',
    due_date: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: '', // Default to empty to force selection
    ref_transaction_number: '',
    bank_account_id: '',
    amount: '',
    tds_amount: '',
    remarks: ''
  });

  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (paymentId) {
        fetchRecord(paymentId);
      } else {
        setIsFetching(false);
        fetchInitialData();
        setFormData({
          project_id: '',
          customer_id: '',
          sales_invoice_id: '',
          invoice_ref: '',
          invoice_date: '',
          due_date: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_mode: '', // Changed to empty to force selection and clear old state
          ref_transaction_number: '',
          bank_account_id: '',
          amount: '',
          tds_amount: '',
          remarks: ''
        });
        setSelectedProject(null);
        setSelectedInvoice(null);
        setInvoices([]);
      }
    }
  }, [isOpen, paymentId]);

  const fetchRecord = async (id) => {
    setIsFetching(true);
    try {
      // Fetch dropdowns first and get the projects list directly
      const projectsList = await fetchInitialData();
      
      const response = await api.get(`/customer-payments/${id}`);
      if (response.data.success) {
        const p = response.data.data;
        setFormData({
          project_id: p.project_id,
          customer_id: p.project?.customer_id || '',
          sales_invoice_id: p.sales_invoice_id,
          payment_date: p.payment_date,
          payment_mode: String(p.payment_mode),
          ref_transaction_number: p.ref_transaction_number || '',
          bank_account_id: p.bank_account_id,
          amount: p.amount,
          tds_amount: p.tds_amount || '',
          remarks: p.remarks || '',
          invoice_ref: p.sales_invoice?.invoice_number || '',
          invoice_date: p.sales_invoice?.invoice_date || '',
          due_date: p.sales_invoice?.due_date || ''
        });
        
        // Find and set project from the freshly fetched list to ensure customer name is available
        const proj = projectsList.find(proj => proj.id === p.project_id);
        if (proj) {
          setSelectedProject(proj);
        } else if (p.project) {
          // Fallback to record's project if not in dropdown list
          setSelectedProject(p.project);
        }
        
        if (p.project_id) {
          fetchInvoices(p.project_id, p.sales_invoice_id);
          fetchInvoiceSummary(p.sales_invoice_id, id);
        }
      }
    } catch (error) {
      console.error('Error fetching record:', error);
      toast.error('Failed to load payment details');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [projRes, bankRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/bank-accounts-dropdown')
      ]);
      const fetchedProjects = projRes.data.data || [];
      setProjects(fetchedProjects);
      setBankAccounts((bankRes.data.data || []).map(b => ({ value: b.id, label: b.bank_name })));
      return fetchedProjects;
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load initial data');
      return [];
    }
  };

  const fetchInvoices = async (projectId, currentInvoiceId = null) => {
    try {
      const res = await api.get('/sales-invoices-dropdown');
      const allInvoices = res.data.data || [];
      const projectInvoices = allInvoices.filter(inv => inv.project_id === projectId);
      const mappedInvoices = projectInvoices.map(inv => ({ 
        value: inv.id, 
        label: inv.invoice_number,
        total_amount: inv.total_amount,
        paid_amount: inv.payments_received_sum_amount || 0,
        due_date: inv.due_date,
        invoice_date: inv.invoice_date
      }));
      setInvoices(mappedInvoices);

      if (currentInvoiceId) {
        const inv = mappedInvoices.find(i => i.value === currentInvoiceId);
        setSelectedInvoice(inv);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };
  
  const fetchInvoiceSummary = async (invoiceId, excludeId = null) => {
    if (!invoiceId) {
      setInvoiceSummary(null);
      return;
    }
    setIsSummaryLoading(true);
    try {
      const [summaryRes, advanceRes] = await Promise.all([
        api.post('/customer-payments/sales-invoice-summary', { sales_invoice_id: invoiceId }),
        excludeId ? api.post('/customer-payments/sales-invoice-advance-amount', {
          sales_invoice_id: invoiceId,
          exclude_payment_id: excludeId
        }) : Promise.resolve(null)
      ]);

      if (summaryRes.data.success) {
        const data = summaryRes.data.data;
        if (advanceRes && advanceRes.data.success) {
          data.total_received_others = advanceRes.data.data.total_payment;
          data.remaining_balance_others = data.invoice_total_amount - advanceRes.data.data.total_payment;
        }
        setInvoiceSummary(data);
      }
    } catch (error) {
      console.error('Error fetching invoice summary:', error);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleProjectChange = (option) => {
    const proj = projects.find(p => p.id === option.value);
    setSelectedProject(proj);
    setFormData(prev => ({ 
      ...prev, 
      project_id: option.value, 
      customer_id: proj ? proj.customer_id : '',
      sales_invoice_id: '',
      invoice_ref: ''
    }));
    setSelectedInvoice(null);
    setInvoiceSummary(null);
    if (option.value) {
      fetchInvoices(option.value);
    } else {
      setInvoices([]);
    }
  };

  const handleInvoiceChange = (option) => {
    const inv = invoices.find(i => i.value === option.value);
    setSelectedInvoice(inv);
    setFormData(prev => ({ 
      ...prev, 
      sales_invoice_id: option.value,
      invoice_ref: inv ? inv.label : '',
      due_date: inv ? inv.due_date : ''
    }));
    if (option.value) {
      fetchInvoiceSummary(option.value, paymentId);
    } else {
      setInvoiceSummary(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date ? date.toISOString().split('T')[0] : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = paymentId 
        ? await api.put(`/customer-payments/${paymentId}`, formData)
        : await api.post('/payments-received', formData);

      if (response.data.success) {
        toast.success(paymentId ? 'Payment updated successfully' : 'Payment received recorded successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(error.response?.data?.message || 'Failed to save payment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <h2>{paymentId ? `Edit Payment Received (REC-${paymentId})` : 'Add Payment Received'}</h2>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <div className={styles['spinner-wrapper']}>
                  <Loader2 size={44} className={styles['spinner']} />
                </div>
                <span className={styles['loading-text']}>Fetching technical details...</span>
              </div>
            ) : (
              <>
            
            {/* Section 1: PO & VENDOR SELECTION (Adapted) */}
            <div className={styles['form-section']}>
              <div className={styles['section-header']}>
                <FileText size={16} />
                <span>Customer & Sales Invoice Selection</span>
              </div>
              <div className={styles['form-grid']}>
                <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                  <label>Project</label>
                  <CustomSelect
                    options={projects.map(p => ({ value: p.id, label: p.project_name }))}
                    value={projects.find(p => p.id === formData.project_id) ? { value: formData.project_id, label: projects.find(p => p.id === formData.project_id).project_name } : null}
                    onChange={handleProjectChange}
                    placeholder="Select Project"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label>Customer</label>
                  <input 
                    type="text" 
                    value={selectedProject ? (selectedProject.customer?.customer_name || 'Loading...') : ''} 
                    disabled 
                    placeholder="Auto-selected"
                  />
                </div>
                <div className={styles['form-group']}>
                   <label>Sales Invoice#</label>
                  <CustomSelect
                    options={invoices}
                    value={invoices.find(i => i.value === formData.sales_invoice_id)}
                    onChange={handleInvoiceChange}
                    placeholder="Select Sales Invoice"
                    isDisabled={!formData.project_id}
                  />
                </div>
                <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                   <label>Sales Invoice Ref</label>
                  <input 
                    type="text" 
                    name="invoice_ref" 
                    value={formData.invoice_ref} 
                    onChange={handleInputChange} 
                    placeholder="INV-001"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label>Date</label>
                  <CustomDatePicker
                    selected={formData.invoice_date}
                    onChange={(date) => handleDateChange('invoice_date', date)}
                    placeholderText="mm/dd/yyyy"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label>Due Date</label>
                  <CustomDatePicker
                    selected={formData.due_date}
                    onChange={(date) => handleDateChange('due_date', date)}
                    placeholderText="mm/dd/yyyy"
                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </div>

                {/* Summary Cards */}
                {(isSummaryLoading || invoiceSummary) && (
                  <div className={[styles['summary-box'], styles['full-width']].join(' ')}>
                    {isSummaryLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#713f12', fontWeight: 600, padding: '4px 0' }}>
                        <Loader2 size={16} className={styles['spinner']} />
                        <span style={{ fontSize: '0.85rem' }}>Updating payment summary...</span>
                      </div>
                    ) : (
                      <>
                        <div className={styles['summary-item']}>
                          <span className={styles['summary-label']}>Sales Invoice Amount</span>
                          <span className={styles['summary-value']}>₹{parseFloat(invoiceSummary.invoice_total_amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                        <div className={styles['summary-item']}>
                          <span className={styles['summary-label']}>{paymentId ? 'Other Received' : 'Received So Far'}</span>
                          <span className={[styles['summary-value'], styles['success']].join(' ')}>
                            ₹{parseFloat(paymentId ? (invoiceSummary.total_received_others || 0) : (invoiceSummary.total_payments_made || 0)).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className={styles['summary-item']}>
                          <span className={styles['summary-label']}>{paymentId ? 'Available' : 'Remaining'}</span>
                          <span 
                            className={[styles['summary-value'], styles['highlight'], styles['clickable']].join(' ')}
                            onClick={() => {
                              const remaining = paymentId ? (invoiceSummary.remaining_balance_others || 0) : (invoiceSummary.remaining_balance || 0);
                              setFormData(prev => ({ ...prev, amount: remaining }));
                            }}
                            title="Click to fill remaining amount"
                          >
                            ₹{parseFloat(paymentId ? (invoiceSummary.remaining_balance_others || 0) : (invoiceSummary.remaining_balance || 0)).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: PAYMENT DETAILS */}
            <div className={styles['form-section']}>
              <div className={styles['section-header']}>
                <CreditCard size={16} />
                <span>Payment Details</span>
              </div>
              <div className={styles['form-grid']}>
                <div className={styles['form-group']}>
                  <label>Payment Date</label>
                  <CustomDatePicker
                    selected={formData.payment_date}
                    onChange={(date) => handleDateChange('payment_date', date)}
                  />
                </div>
                <div className={styles['form-group']}>
                  <label>Payment Mode</label>
                  <CustomSelect
                    options={[
                      { value: '1', label: 'Cash' },
                      { value: '2', label: 'Bank Transfer' },
                      { value: '3', label: 'Cheque' },
                      { value: '4', label: 'Online' }
                    ]}
                    value={[
                      { value: '1', label: 'Cash' },
                      { value: '2', label: 'Bank Transfer' },
                      { value: '3', label: 'Cheque' },
                      { value: '4', label: 'Online' }
                    ].find(opt => opt.value === formData.payment_mode)}
                    onChange={(option) => setFormData(prev => ({ ...prev, payment_mode: option.value }))}
                    placeholder="Select Payment Mode"
                  />
                </div>
                {formData.payment_mode !== '1' && (
                  <>
                    <div className={styles['form-group']}>
                      <label>Ref#</label>
                      <input
                        type="text"
                        name="ref_transaction_number"
                        value={formData.ref_transaction_number}
                        onChange={handleInputChange}
                        placeholder="TXN-9821"
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label>Bank Account</label>
                      <CustomSelect
                        options={bankAccounts}
                        value={bankAccounts.find(b => b.value === formData.bank_account_id)}
                        onChange={(option) => setFormData(prev => ({ ...prev, bank_account_id: option.value }))}
                        placeholder="Select Bank Account"
                      />
                    </div>
                  </>
                )}
                <div className={styles['form-group']}>
                  <label>Amount Reciving</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                  />
                </div>
                {formData.payment_mode !== '1' && (
                  <div className={styles['form-group']}>
                    <label>TDS (if applicable)</label>
                    <input
                      type="number"
                      name="tds_amount"
                      value={formData.tds_amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                    />
                  </div>
                )}
                <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                  <label>Remarks / Notes</label>
                  <textarea 
                    name="remarks" 
                    value={formData.remarks} 
                    onChange={handleInputChange} 
                    placeholder="Enter any internal notes..."
                    rows={3}
                    className={styles['native-textarea']}
                  />
                </div>
                </div>
              </div>
            </>
          )}
        </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={[styles['primary-footer-btn'], styles['mark-paid-btn']].join(' ')} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <CheckCircle2 size={18} />}
              <span>{loading ? (paymentId ? 'Updating...' : 'Recording...') : (paymentId ? 'Update Payment' : 'Save Payment')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentReceivedDrawer;
